import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ColumnSearch from "../../../components/ColumnSearch";
import { XMarkIcon } from "@heroicons/react/20/solid";
import useDialogStore from "../../../stores/useDialogStore";
import ClientForm from "./ClientForm";
import EditableCell from "../../../components/EditableCell";
import { toast } from "react-toastify";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import { PlusIcon } from "@heroicons/react/20/solid";

const MARKET_TYPE_LABELS = {
  CH: "Switzerland (CH)",
  EX: "International (EX)",
};

export default function ClientsList() {
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const { data: clients, isLoading } = useQuery({
    queryKey: ["clients", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      // Ensure sort parameters are properly formatted
      const sortBy = params.get("sortBy");
      const order = params.get("order");

      if (sortBy) {
        params.set("sortBy", sortBy);
        params.set("order", order || "asc");
      }

      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/clients?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/clients/${id}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["clients"]);
      toast.success("Client deleted successfully");
      closeDialog();
    },
    onError: (error) => {
      console.error("Delete client error:", error);
    },
  });

  const handleDelete = (id) => {
    openDialog({
      title: "Confirm Deletion",
      content: (
        <div>
          <p>Are you sure you want to delete this client?</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={closeDialog}
              variant="secondary"
              size="sm"
              className="p-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                deleteMutation.mutate(id);
              }}
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              className="p-1"
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    // Force refetch when filters change
    queryClient.invalidateQueries(["clients"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    // Ensure we're using the correct parameter names for the API
    const sortBy = params.get("sortBy");
    const order = params.get("order");

    // Update the URL with the correct parameters
    const newParams = new URLSearchParams();
    if (sortBy) newParams.set("sortBy", sortBy);
    if (order) newParams.set("order", order);

    // Preserve other existing parameters
    for (const [key, value] of params.entries()) {
      if (key !== "sortBy" && key !== "order") {
        newParams.set(key, value);
      }
    }

    navigate(`${location.pathname}?${newParams.toString()}`);
    queryClient.invalidateQueries(["clients"]);
  };

  const handleClearFilters = () => {
    // Reset sort config
    setSortConfig({
      key: null,
      direction: "desc",
    });

    // Clear URL parameters and navigate to base URL
    navigate(location.pathname);

    // Force refetch the data
    queryClient.invalidateQueries(["clients"]);
  };

  const editCellMutation = useMutation({
    mutationFn: ({ clientId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/clients/${clientId}`,
        { [field]: value }
      ),

    onSuccess: () => {
      queryClient.invalidateQueries(["clients"]);
      toast.success("Client updated successfully");
    },
    onError: (error) => {
      console.error("Edit cell error:", error);
      toast.error("Failed to update client");
    },
  });

  const handleOpenClientForm = (client = null) => {
    openDialog({
      title: client ? "Edit Client" : "Add New Client",
      content: (
        <ClientForm
          payload={client}
          onSuccess={() => {
            closeDialog();
            queryClient.invalidateQueries(["clients"]);
          }}
        />
      ),
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Clients List
                </h4>
                <div className="flex flex-col gap-2 lg:flex-row items-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>

                  <Button
                    variant="outline-info"
                    size="sm"
                    icon={<PlusIcon className="h-4 w-4" />}
                    onClick={() => handleOpenClientForm()}
                  >
                    Register Client
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Full Name"
                            columnKey="name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by name..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Unique ID"
                            columnKey="unique_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Unique ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Market Type"
                            columnKey="market_type"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Market Type..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : clients?.data?.data?.length == 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <NoData message="No clients found" />
                        </td>
                      </tr>
                    ) : (
                      clients?.data?.data?.map((client, index) => (
                        <tr
                          key={client.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={client.name || "—"}
                                displayValue={client.name || "—"}
                                id={client.id}
                                field="name"
                                maxLength={50}
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    clientId: data.id,
                                    field: "name",
                                    value: data.value,
                                  })
                                }
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={client.unique_id || "—"}
                                displayValue={client.unique_id || "—"}
                                id={client.id}
                                field="unique_id"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    clientId: data.id,
                                    field: "unique_id",
                                    value: data.value,
                                  })
                                }
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={client.market_type || "—"}
                                displayValue={
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      client.market_type === "CH"
                                        ? "bg-red-100 text-red-800"
                                        : client.market_type === "EX"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {MARKET_TYPE_LABELS[client.market_type] ||
                                      "—"}
                                  </span>
                                }
                                id={client.id}
                                field="market_type"
                                type="select"
                                options={[
                                  { value: "CH", label: "Switzerland (CH)" },
                                  { value: "EX", label: "International (EX)" },
                                ]}
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    clientId: data.id,
                                    field: "market_type",
                                    value: data.value,
                                  })
                                }
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4 gap-2">
                              <Button
                                onClick={() => handleOpenClientForm(client)}
                                variant="outline-info"
                                size="icon"
                                icon={<PencilSquareIcon className="h-4 w-4" />}
                                className="p-1"
                              >
                                <PencilSquareIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                              </Button>
                              {user.role === "SuperAdmin" && (
                                <Button
                                  onClick={() => handleDelete(client.id)}
                                  variant="outline-danger"
                                  size="icon"
                                  icon={<TrashIcon className="h-4 w-4" />}
                                  className="p-1"
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={clients?.data?.current_page}
                last_page={clients?.data?.last_page}
                next_page_url={clients?.data?.next_page_url}
                prev_page_url={clients?.data?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}